import {
  CallDriverIcon,
  CheckDriverLogsIcon,
  DotPrecheckIcon,
  MessageDriverIcon,
} from '@mgk-eld/assets';
import { actionSuccess, green, grey, purple } from '@mgk-eld/utils';
import { LoadingButton } from '@mui/lab';
import { lighten, styled, SxProps } from '@mui/material';
import Button from '@mui/material/Button';
import dayjs from 'dayjs';
import { useMemo } from 'react';

export const CustomActionButton = styled(Button)(({ theme }) => ({
  width: 127,
  height: 40,
  borderRadius: 8,

  '&:hover': {
    background: lighten(theme.palette.primary.light, 0.1),
    color: 'white !important',
    borderColor: lighten(theme.palette.primary.light, 0.1),

    'span svg path': {
      transition: 'all 0.3s ease',
      fill: 'white !important',
    },
  },

  'span svg path': {
    transition: 'all 0.3s ease',
  },

  '&.Mui-disabled': {
    opacity: 0.5, // Optional, for visual feedback
    color: theme.palette.action.disabled,

    'span svg path': {
      fill: theme.palette.action.disabled, // Set disabled icon color explicitly
    },
  },
}));

export const CustomButton = styled(Button)<{
  selected?: boolean;
  width?: number;
  disabled?: boolean;
}>`
  width: ${(props) => props.width ?? 94}px;
  height: 32px;
  border-radius: 8px;
  border: ${(props) => (props.selected ? '1.5px solid #E6EFF7' : 'none')};

  &:hover {
    background: ${(props) => lighten(props.theme.palette.secondary.light, 0.1)};
    border: ${(props) =>
      props.selected
        ? `1.5px solid ${props.theme.palette.secondary.main}`
        : 'none'};
    color: white;
  }

  background: ${(props) =>
    props.selected
      ? props.theme.palette.background.paper
      : props.theme.palette.secondary.main};
  color: ${(props) =>
    props.selected
      ? props.theme.palette.text.primary
      : props.theme.palette.secondary.contrastText};

  ${(props) =>
    props.disabled &&
    `
    color: white !important;
    opacity: 0.3;
    pointer-events: none;
  `}
`;

export const ReportButton = styled(LoadingButton)`
  border-radius: 8px;
  padding: 11px 40px;
`;

export const LogsReportButton = styled(LoadingButton)`
  border-radius: 8px;
  padding: 8px 40px;
`;

export const CheckLogsButton: React.FC<{
  driverId: number;
  date: string;
  company_id: number;
  disabled?: boolean;
  backDays?: number;
  fullHeight?: boolean;
  fullWidth?: boolean;
  sx?: SxProps;
}> = ({
  driverId,
  date,
  company_id,
  disabled = false,
  backDays = 8,
  fullHeight,
  fullWidth,
  sx,
}) => {
  const [startDate, endDate] = useMemo(() => {
    const end = dayjs(date).format('YYYY-MM-DD');
    const start = dayjs(date).subtract(backDays, 'days').format('YYYY-MM-DD');
    return [start, end];
  }, [date, backDays]);

  return (
    <Button
      sx={{
        borderRadius: '8px',
        minHeight: fullHeight ? '80%' : '38px',
        minWidth: '40px',
        padding: '6px',
        ...sx,
      }}
      fullWidth={fullWidth}
      color="secondary"
      variant="contained"
      disabled={disabled}
      href={`#/bulk?company_id=${company_id}&driver_id=${driverId}&startDate=${startDate}&endDate=${endDate}`}
      target="_blank"
      rel="noreferrer"
    >
      <CheckDriverLogsIcon />
    </Button>
  );
};

export const CallDriverButton: React.FC<{
  phone: string;
  fullHeight?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: SxProps;
}> = ({ phone, fullHeight, fullWidth, sx, disabled }) => {
  return (
    <Button
      sx={{
        background: green,
        borderRadius: '8px',

        minHeight: fullHeight ? '80%' : '38px',
        minWidth: '40px',
        padding: '6px',

        ':hover': {
          background: actionSuccess,
        },

        ...sx,
      }}
      fullWidth={fullWidth}
      variant="contained"
      disabled={disabled}
    >
      <a
        style={{ textDecoration: 'none', color: 'inherit', height: '17px' }}
        href={`tel:${phone}`}
      >
        <CallDriverIcon />
      </a>
    </Button>
  );
};

export const MessageDriverButton: React.FC<{
  tg_link: string;
  fullHeight?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: SxProps;
}> = ({ tg_link, fullHeight, fullWidth, sx, disabled }) => {
  return (
    <Button
      sx={{
        background: purple,
        borderRadius: '8px',

        minHeight: fullHeight ? '80%' : '38px',
        minWidth: '40px',
        padding: '6px',

        ':hover': {
          background: grey[1000],
        },

        ...sx,
      }}
      fullWidth={fullWidth}
      variant="contained"
      disabled={disabled}
      href={tg_link}
      target="_blank"
    >
      <MessageDriverIcon />
    </Button>
  );
};

export const DotPrecheckButton: React.FC<{
  handleClick: () => void;
  fullHeight?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  variant?: 'contained' | 'text' | 'outlined';
  sx?: SxProps;
}> = ({ handleClick, disabled, fullHeight, fullWidth, sx, variant }) => {
  return (
    <Button
      sx={{
        borderRadius: '8px',
        height: fullHeight ? '100%' : 'auto',

        ...sx,
      }}
      variant={variant}
      onClick={handleClick}
      disabled={disabled}
      fullWidth={fullWidth}
    >
      <DotPrecheckIcon />
    </Button>
  );
};
